/**
 * Entry point for the FpS Content Generator frontend application.
 * Initializes the React application and attaches it to the DOM element with id 'asw'.
 * Uses React 18's hydrateRoot for server-side rendered content and createRoot for client-side rendering.
 */

import React from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import "./styles/index.css";
import { initializeFirebase } from "./config/firebaseConfig";
import { UserDetailsProvider } from "./context/UserDetailsContext";
import App from "./components/App";
import log from "./config/logging";
import reportWebVitals from "./reportWebVitals";

const environment = process.env.REACT_APP_ENV || "development";
log.debug(`Application has started in ${environment} environment`);

/**
 * Adds a sub-brand to the site logo and sets the fill color for the logo SVG.
 */
const addSubBrandToSiteLogo = () => {
  const brandElement = document.getElementById("brand");
  const mastheadHeight = "4.5vw";

  if (brandElement) {
    brandElement.style.display = "flex";
    brandElement.style.flexWrap = "nowrap";

    const style = document.createElement("style");
    style.textContent = `
      #brand::after {
        position: relative;
        top: 0.21em;
        align-self: center;
        width: min-content;
        margin-left: 0.5em;
        color: #fff;
        font-size: clamp(0.55730465952rem, 0.82066268401vw, 0.82066268401vw);
        line-height: 0.8;
        content: "content generator";
      }

      #masthead #brand svg:has(#fps-logo) {
        fill: #fff !important;
      }

      #masthead #brand svg:has(#fps-logo) use[href="#fps-logo"] {
        display: inline !important;
      }

      #masthead {
        height: ${mastheadHeight} !important;
        min-height: 60px;
        padding: 0 !important;
      }

      #masthead:before {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        bottom: auto !important;
        left: 0 !important;
        z-index: -10 !important;
        height: ${mastheadHeight} !important;
        min-height: 60px;
        background-color: #0D47A1 !important;
        content: "" !important;
      }

      #masthead > a:hover {
        text-decoration: none !important;
      }

      #main > article {
        margin-top: max(${mastheadHeight}, 60px);
      }
    `;
    document.head.appendChild(style);
  }
};

/**
 * Updates the brand link.
 */
const updateBrandLink = () => {
  const masthead = document.getElementById("masthead");
  const brand = document.getElementById("brand");
  const existingLink = brand?.querySelector("a");

  if (masthead && brand && existingLink) {
    // Create new link element
    const newLink = document.createElement("a");
    newLink.href = "/generatore-content-ai";
    newLink.rel = "home";

    // Move brand inside the new link
    newLink.appendChild(brand.cloneNode(true));

    // Replace old structure with new one
    masthead.replaceChild(newLink, brand);

    // Remove the old nested link if it exists
    const nestedLink = newLink.querySelector("a");
    if (nestedLink) {
      const svg = nestedLink.querySelector("svg");
      if (svg) {
        nestedLink.parentNode?.insertBefore(svg, nestedLink);
      }
      nestedLink.remove();
    }
  }
};

addSubBrandToSiteLogo();
updateBrandLink();

const rootElement = document.getElementById("asw") as HTMLElement;
const siteNavigation = document.getElementById(
  "site-navigation"
) as HTMLElement;

initializeFirebase();

/**
 * Adds the 'asw-app' class to the specified elements if they exist.
 * @param elements - An array of elements or null values to add the class to.
 */
const addAswAppClass = (...elements: (Element | null)[]) => {
  elements.forEach((element) => element?.classList.add("asw-app"));
};

addAswAppClass(rootElement, siteNavigation);

const renderApp = () => (
  <React.StrictMode>
    <UserDetailsProvider>
      <App />
    </UserDetailsProvider>
  </React.StrictMode>
);

if (rootElement && siteNavigation) {
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, renderApp());
  } else {
    createRoot(rootElement).render(renderApp());
  }
} else {
  log.error("Required DOM elements not found. App cannot be rendered.");
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(log.debug);
